import * as React from 'react';
import classnames from 'classnames';
import { Check } from 'lucide-react';
import { t } from '~/i18n';
import { useOutsideClick } from '~hooks/useOutsideClick';
import menu from '~styles/dropdownMenu.module.css';
import { countOrgs, filterOrgTree, OrgTreeNode } from './orgTree';
import styles from './OrganizationSelect.module.css';

// Below this the list is faster to scan than to type into, and the field would
// just be chrome. Same threshold as the site selector.
const SEARCH_THRESHOLD = 8;

export interface OrganizationSelectProps {
  tree: OrgTreeNode[];
  value: string | null;
  onChange: (id: string) => void;
  /** Sits above the tree, outside it — the orphan-site bucket belongs to no
   *  customer, so it can't hang under one. Omitted for org admins. */
  extraOption?: { value: string; label: string };
}

/** Which organization the page is about. A tree rather than a list: a
 *  sub-organization's name is only meaningful under its parent — several
 *  customers have one called "OCMW" — and a filtered list drops exactly the
 *  context that tells them apart. So the menu keeps the nesting, and a search
 *  keeps the ancestors of whatever it matched, like the site selector. */
const OrganizationSelect: React.FC<OrganizationSelectProps> = ({ tree, value, onChange, extraOption }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const outsideRefs = React.useMemo(() => [wrapperRef, menuRef], []);
  useOutsideClick(outsideRefs, () => setOpen(false), open);

  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Land on the search box when there is one — that's the fast path through a
  // long list. ArrowDown from there walks into the rows as usual.
  React.useEffect(() => {
    if (!open) return;
    if (searchRef.current) {
      searchRef.current.focus();
      return;
    }
    menuRef.current?.querySelector<HTMLButtonElement>('button[role="option"]')?.focus();
  }, [open]);

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? filterOrgTree(tree, needle) : tree;
  }, [tree, query]);

  const extraMatches = !!extraOption && extraOption.label.toLowerCase().includes(query.trim().toLowerCase());

  const selectedLabel = React.useMemo(() => {
    if (!value) return null;
    if (extraOption && value === extraOption.value) return extraOption.label;
    const find = (nodes: OrgTreeNode[]): string | null => {
      for (const node of nodes) {
        if (node.id === value) return node.name;
        const found = find(node.children);
        if (found) return found;
      }
      return null;
    };
    return find(tree);
  }, [value, tree, extraOption]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const select = (id: string) => {
    close();
    onChange(id);
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button[role="option"]') ?? []);
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'ArrowDown' ? items[(idx + 1) % items.length] : items[(idx - 1 + items.length) % items.length];
    next?.focus();
  };

  const renderRow = (id: string, label: string) => {
    const isSelected = id === value;
    return (
      <button
        type="button"
        role="option"
        aria-selected={isSelected}
        className={classnames(menu.MenuItem, styles.Row, isSelected && styles.RowSelected)}
        onClick={() => select(id)}
      >
        <span className={styles.RowName}>{label}</span>
        {isSelected && (
          <span className={menu.Check} aria-hidden="true">
            <Check size={14} />
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={styles.Wrapper} ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className={classnames(styles.Trigger, open && styles.TriggerOpen)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('billing.organization')}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={classnames(styles.TriggerLabel, !selectedLabel && styles.TriggerPlaceholder)}>
          {selectedLabel ?? t('site.form.organizationPlaceholder')}
        </span>
        <span className={styles.Caret} aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div ref={menuRef} className={classnames(menu.Menu, menu.MenuLeft, styles.Menu)} onKeyDown={handleMenuKeyDown}>
          {countOrgs(tree) > SEARCH_THRESHOLD && (
            <div className={styles.SearchRow}>
              <input
                ref={searchRef}
                type="text"
                className={styles.Search}
                value={query}
                placeholder={t('profiles.filters.search')}
                aria-label={t('profiles.filters.search')}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}
          {filtered.length === 0 && !extraMatches ? (
            <div className={styles.NoResults}>{t('global.noResults')}</div>
          ) : (
            <ul className={styles.List} role="listbox" aria-label={t('billing.organization')}>
              {extraOption && extraMatches && <li>{renderRow(extraOption.value, extraOption.label)}</li>}
              {filtered.map((node) => (
                <OrgBranch key={node.id} node={node} renderRow={renderRow} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

interface OrgBranchProps {
  node: OrgTreeNode;
  renderRow: (id: string, label: string) => React.ReactNode;
}

/** One organization and everything under it. Nested lists rather than computed
 *  indentation, so each level draws its own guide rail and screen readers get
 *  the same grouping sighted users see — the shape the site selector uses. */
const OrgBranch: React.FC<OrgBranchProps> = ({ node, renderRow }) => (
  <li>
    {renderRow(node.id, node.name)}
    {node.children.length > 0 && (
      <ul role="group" aria-label={node.name} className={styles.Group}>
        {node.children.map((child) => (
          <OrgBranch key={child.id} node={child} renderRow={renderRow} />
        ))}
      </ul>
    )}
  </li>
);

export default OrganizationSelect;
